<template>
  <Section
    :id="id"
    :header="t('common.rating')"
    :loading="isAnyLoading"
    class="route-builder-rate-shop phase-2"
  >
    <template #backButton>
      <RygenButton
        mode="tertiary"
        class="back-button"
        :icon="PrimeIcons.ARROW_LEFT"
        :label="t('common.backToParam', { param: t('routeBuilder.planning') })"
        @click="emit('back')"
      />
    </template>

    <template #default>
      <RouteBuilderPhase2RateShopCard
        id="preferred-rates"
        v-model:expanded="expansionStates['preferred']"
        type="preferred"
        :mileage="preferredQuotesMileage"
        :rate-quotes="preferredQuotes"
        :current-step="request.currentStep"
        @select="onRateGroupSelect"
      />

      <RouteBuilderPhase2RateShopCard
        id="fastest-rates"
        v-model:expanded="expansionStates['fastest']"
        type="fastest"
        :mileage="fastestQuotesMileage"
        :rate-quotes="fastestQuotes"
        :current-step="request.currentStep"
        @select="onRateGroupSelect"
      />

      <RouteBuilderPhase2RateShopCard
        id="cheapest-rates"
        v-model:expanded="expansionStates['cheapest']"
        type="cheapest"
        :mileage="cheapestQuotesMileage"
        :rate-quotes="cheapestQuotes"
        :current-step="request.currentStep"
        @select="onRateGroupSelect"
      />

      <RateSelectionReasonModal
        v-model:visible="lowestCostReasonModal.visible"
        :lowest-price-quote="lowestCostReasonModal.lowestCostRateQuote"
        :selected-quote="lowestCostReasonModal.selectedRateQuote"
        @confirm="onRateSelectionConfirmed"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import type { PropType, Ref } from 'vue';
import { computed, onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useLowestCostReasonCode } from '@/composables';
import {
  RateQuote,
  RateSelectionReason,
  RateUsage,
  RoutingGuideClassification,
} from '@/model';
import {
  ProposedShipmentRateResponse,
  RateQuoteGroupType,
} from '@/model/routeBuilder';
import {
  RouteBuilderPhase2Request,
  RoutePlanProposedShipment,
  RoutePlanSelectRateGroupPayload,
  RoutePlanShipmentRequest,
} from '@/model/routeBuilder/phase2';

import RateSelectionReasonModal from '@/components/rateShop/RateSelectionReasonModal.vue';
import RouteBuilderPhase2RateShopCard from '@/components/routeBuilder/rateShop/RouteBuilderPhase2RateShopCard.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  request: {
    type: Object as PropType<RouteBuilderPhase2Request>,
    required: true,
  },
});
const { id, request } = toRefs(props);
const emit = defineEmits<{
  (event: 'back'): void;
  (event: 'select-group', payload: RoutePlanSelectRateGroupPayload): void;
}>();

const { t } = useI18n();
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();
const toast = useToast();

const rateShops: Ref<Array<Loadable<ProposedShipmentRateResponse>>> = ref([]);

const expansionStates: Ref<Record<RateQuoteGroupType, boolean>> = ref({
  preferred: true,
  fastest: false,
  cheapest: false,
  none: false,
});

const lowestCostReasonModal = ref({
  visible: false,
  rateSelectionReason: undefined as RateSelectionReason | undefined,
  selectedRateQuote: new RateQuote(),
  lowestCostRateQuote: new RateQuote(),
  selectRateGroupPayload: { type: 'none' } as RoutePlanSelectRateGroupPayload,
});

const isAnyLoading = computed(() => rateShops.value.some((r) => r.loading));

const preferredQuotes: Ref<Array<Loadable<RoutePlanShipmentRequest>>> =
  computed(() => {
    return rateShops.value.map(mapRateResponseToSingleQuote('preferred'));
  });

const fastestQuotes: Ref<Array<Loadable<RoutePlanShipmentRequest>>> = computed(
  () => {
    return rateShops.value.map(mapRateResponseToSingleQuote('fastest'));
  },
);

const cheapestQuotes: Ref<Array<Loadable<RoutePlanShipmentRequest>>> = computed(
  () => {
    return rateShops.value.map(mapRateResponseToSingleQuote('cheapest'));
  },
);

const preferredQuotesMileage = computed(() => {
  return sumMileageForRateQuoteGroup(preferredQuotes.value);
});

const fastestQuotesMileage = computed(() => {
  return sumMileageForRateQuoteGroup(fastestQuotes.value);
});

const cheapestQuotesMileage = computed(() => {
  return sumMileageForRateQuoteGroup(cheapestQuotes.value);
});

const rateQuoteGroups: Ref<
  Record<RateQuoteGroupType, Array<Loadable<RoutePlanShipmentRequest>>>
> = computed(() => ({
  preferred: preferredQuotes.value,
  fastest: fastestQuotes.value,
  cheapest: cheapestQuotes.value,
  none: [],
}));

/**
 * Load rates for a single `ProposedShipment`
 *
 * This function uses `then` instead of `await` because we don't want to wait on
 * any single rate request; we want them all to run in parallel.
 */
const loadRatesForShipment = (
  shipment: RoutePlanProposedShipment,
  index: number,
) => {
  if (!rateShops.value[index]) {
    rateShops.value[index] = { loading: true };
  }

  rateShops.value[index].loading = true;
  api.routePlan
    .rateRoutePlanProposedShipment(shipment)
    .then((r) => {
      rateShops.value[index].data = r;
    })
    .catch((e) => {
      rateShops.value[index].error = e;
      console.error(`Error rating proposed shipment ${shipment.identifier}`, e);
      toast.addError({
        detail: t('errors.loadingParamForParamWithId', {
          param: t('rateShop.rates', 2),
          param2: t('shipments.shipments'),
          id: shipment.identifier,
        }),
        error: e,
      });
    })
    .finally(() => {
      rateShops.value[index].loading = false;
    });
};

const loadRatesForAllShipments = (shipments: RoutePlanProposedShipment[]) => {
  shipments.forEach((shipment, index) => loadRatesForShipment(shipment, index));
};

const findProposedShipment = (shipmentIdentifier?: number) => {
  if (!shipmentIdentifier) {
    return undefined;
  }

  const { proposedShipment } =
    request.value.findProposedShipment(shipmentIdentifier);
  return proposedShipment;
};

/**
 * Create a predicate for use in `Array.Map` to map an entire rate shop to a
 * single rate quote
 */
const mapRateResponseToSingleQuote = (type: RateQuoteGroupType) => {
  return ({ data, loading, error }: Loadable<ProposedShipmentRateResponse>) => {
    if (loading) {
      return { loading };
    }

    const proposedShipment = findProposedShipment(data?.shipmentIdentifier);
    if (!proposedShipment) {
      const msg = `Could not find proposed shipment ${data?.shipmentIdentifier}`;
      console.warn(msg);

      return {
        loading,
        error: msg,
      };
    }

    const rateQuote = data?.getQuoteForType(type);
    const routingGuideRule = data?.rateShop.routingGuideRule;

    return {
      loading,
      error,
      data: new RoutePlanShipmentRequest({
        proposedShipment,
        rateQuote,
        routingGuideRule,
      }),
    };
  };
};

const onRateGroupSelect = (type: RateQuoteGroupType) => {
  if (type === 'none') {
    throw new Error('`none` is not a selectable RateQuoteGroupType');
  }

  const rates = rateQuoteGroups.value[type].map(({ data }, index) => {
    if (!data) {
      throw new Error(
        `Missing selected rate for a proposed shipment (index: ${index}). This shouldn't be able to happen`,
      );
    }

    return data;
  });

  const selectRateGroupPayload = { type, rates };
  if (isRateSelectionReasonRequired.value) {
    for (let i = 0; i < request.value.shipmentRequests.length; i++) {
      const s = request.value.shipmentRequests[i].proposedShipment;
      const selectedRateQuote = rates[i].rateQuote;
      if (!selectedRateQuote) {
        console.warn(
          `Missing selected rate for a proposed shipment ${s.identifier} (index: ${i})`,
        );
        continue;
      }

      const rateQuotes = rateShops.value[i].data?.rateShop?.rateQuotes ?? [];
      const lowestCostRateQuote = rateQuotes
        .filter(
          (r) =>
            r.routingGuideClassification !==
            RoutingGuideClassification.EXCLUDED,
        )
        .filter((r) => r.rateUsage === RateUsage.RATE_SHOP)
        .sort((a, b) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0))
        .at(0);
      if (!lowestCostRateQuote) {
        console.warn(
          `Missing lowest cost rate for a proposed shipment ${s.identifier} (index: ${i})`,
        );
        continue;
      }

      const lowestCost = lowestCostRateQuote.totalPrice ?? 0;
      if (
        selectedRateQuote.totalPrice &&
        selectedRateQuote.totalPrice > lowestCost
      ) {
        lowestCostReasonModal.value.selectedRateQuote = selectedRateQuote;
        lowestCostReasonModal.value.lowestCostRateQuote = lowestCostRateQuote;
        lowestCostReasonModal.value.selectRateGroupPayload =
          selectRateGroupPayload;
        lowestCostReasonModal.value.visible = true;
        return;
      }
    }
  }

  // rate selection reason wasn't required, so just update request
  emit('select-group', selectRateGroupPayload);
};

const onRateSelectionConfirmed = (payload: RateSelectionReason) => {
  if (!payload.rateSelectionReasonType) {
    console.warn('no rate selection reason');
    return;
  }

  const rateSelectionReason = new RateSelectionReason(payload);

  const selectRateGroupPayload = {
    ...lowestCostReasonModal.value.selectRateGroupPayload,
    rateSelectionReason,
  };
  emit('select-group', selectRateGroupPayload);
};

const sumMileageForRateQuoteGroup = (
  rates: Array<Loadable<RoutePlanShipmentRequest>>,
) => {
  return rates
    .map((r) => r.data?.rateQuote?.totalMileage ?? 0)
    .reduce((a, b) => a + b, 0);
};

onMounted(() => {
  loadRatesForAllShipments(request.value.proposedShipments);
});
</script>

<style>
.route-builder-rate-shop.phase-2 {
  .section-header {
    .section-header-left {
      flex-flow: column;
      align-items: flex-start;
    }
  }

  .section-data {
    display: flex;
    flex-flow: column;
    gap: var(--gap-1);
    height: 100%;
    overflow: auto;
    padding: var(--route-builder-rate-shop-padding);
  }
}
</style>
